import {
  ContractTypes,
  DeployContractPackageResult,
  NFTTokenMetadataEntry,
  NFTTokenMetadataWithLinks,
  NFTTokenResult,
  OwnershipModeType,
} from '@block-explorer/entities';
import { formatDate, NamedKeyPrefix } from '@make-software/cspr-design';
import { buildRouterPath } from '../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../router/paths';
import { getExpirationDate } from '../redux/utils';

export const isUrl = (value: string): boolean => {
  return new RegExp('^(https)|(http)').test(value);
};

export const tryParseJSONObject = (jsonString) => {
  try {
    const o = JSON.parse(jsonString);

    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) {
    return false;
  }

  return false;
};

export const splitKeyOnWords = (key: string): string => {
  const alwaysUpperCased = ['URL', 'URI', 'ID', 'NFT', 'IPFS'];
  const camelCaseRegExp = new RegExp('([A-Z][a-z]+)', 'g');
  const camelCaseSubstitution = `\x20$1`; // added space before every founded capital letter
  const snakeCaseRegExp = new RegExp('(_)', 'g');
  const snakeCaseSubstitution = `\x20`; // replace _ with space

  if (camelCaseRegExp.test(key)) {
    return key
      .replace(camelCaseRegExp, camelCaseSubstitution)
      .split('\x20')
      .map((word) => {
        return (
          alwaysUpperCased.find((u) => word && new RegExp(word, 'i').test(u)) ||
          word
        );
      })
      .join('\x20');
  }
  if (snakeCaseRegExp.test(key)) {
    return key.replace(snakeCaseRegExp, snakeCaseSubstitution);
  }

  return alwaysUpperCased.find((u) => new RegExp(key, 'i').test(u)) || key;
};

export const convertIpfsSourceAsLink = (
  metadata: NFTTokenMetadataEntry
): NFTTokenMetadataEntry => {
  const isIpfsKey = new RegExp(`ipfs`, 'gi').test(metadata.key);
  const isIpfsValue = new RegExp(`ipfs://`, 'gi').test(metadata.value);
  const isLinkValue = new RegExp(`http://|https://`, 'gi').test(metadata.value);
  const isContractHash = new RegExp(NamedKeyPrefix.HASH, 'gi').test(
    metadata.value
  );

  let value = metadata.value;
  if (isIpfsValue) {
    value = metadata.value.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }
  if (isIpfsKey) {
    value = isLinkValue
      ? metadata.value
      : 'https://ipfs.io/ipfs/' + metadata.value;
  }
  if (isContractHash) {
    const hash = value.replace(NamedKeyPrefix.HASH, '');
    value =
      window.location.origin +
      buildRouterPath(CONTRACT_PACKAGE_PATH, { id: hash }, undefined);
  }

  return {
    key: metadata.key,
    value: value,
  };
};

export const findMediaPreview = (metadata: NFTTokenMetadataEntry): boolean => {
  const hasImageExtension = new RegExp(
    /\w+\.(jpg|jpeg|png|svg|gif)/,
    'gi'
  ).test(metadata.value);
  const knownImageKey = new RegExp(
    `pictureIpfs|image|imageUrl|image_url|asset|ipfs_url|token_uri`,
    'gi'
  ).test(metadata.key);

  return hasImageExtension || knownImageKey;
};

export const listToDictionary = <T extends any>(obj): T => {
  return Object.entries(obj).map((item) => {
    if (item[1] === null) {
      return {
        key: item[0] as string,
        value: null,
      };
    }

    const value =
      typeof item[1] === 'object' && item[0] !== 'attributes'
        ? JSON.stringify(item[1])
        : item[1];

    return {
      key: item[0],
      value,
    };
  }) as T;
};

export const deriveMetadataFromToken = (
  nftToken: NFTTokenResult
): NFTTokenMetadataEntry[] => {
  const onChain =
    nftToken && nftToken?.onchain_metadata ? nftToken?.onchain_metadata : {};
  const offChain =
    nftToken && nftToken?.offchain_metadata ? nftToken?.offchain_metadata : {};

  // Order here is important
  return [
    ...listToDictionary<any>(offChain),
    ...listToDictionary<any>(onChain),
  ];
};

export const getNftTokenDetails = (
  data: NFTTokenResult | undefined
): NFTTokenMetadataWithLinks => {
  const nftTokenMetadata = data ? deriveMetadataFromToken(data) : null;

  if (!nftTokenMetadata) {
    return {
      itemsWithLinks: [],
      attributes: null,
    } as NFTTokenMetadataWithLinks;
  }

  const nftTokenMetadataWithLinks = nftTokenMetadata.reduce(
    (acc, item) => {
      if (item.key === 'attributes') {
        acc.attributes = item;
      } else if (item.key === 'expiration') {
        const ms = Number(item.value / 1000);
        const date = new Date(ms);
        const time = formatDate(getExpirationDate({ timestamp: date }));
        acc.itemsWithLinks.push({ key: item.key, value: time });
      } else {
        const converted = convertIpfsSourceAsLink(item);
        if (!tryParseJSONObject(converted.value)) {
          acc.itemsWithLinks.push(converted);
        }
      }
      return acc;
    },
    { itemsWithLinks: [] as NFTTokenMetadataEntry[], attributes: null }
  );

  return nftTokenMetadataWithLinks;
};

export const isNFTTradable = (
  contractPackage: DeployContractPackageResult | null | undefined
) => {
  if (contractPackage == undefined) {
    return false;
  }

  const isNFT =
    contractPackage?.latest_version_contract_type_id != null &&
    [
      ContractTypes.CEP95Nft,
      ContractTypes.CEP47Nft,
      ContractTypes.CustomCEP47Nft,
    ].includes(contractPackage?.latest_version_contract_type_id);

  const isCEP78NFT =
    contractPackage?.latest_version_contract_type_id != null &&
    [ContractTypes.CEP78Nft, ContractTypes.CustomCEP78Nft].includes(
      contractPackage?.latest_version_contract_type_id
    );

  const isCEP78Tradable =
    isCEP78NFT &&
    Number(contractPackage?.metadata?.ownership_mode) ===
      OwnershipModeType.Transferable;

  return isNFT || isCEP78Tradable;
};
